// re.rejouis.fr — formule de calcul du crédit de reprise.
// SOURCE DE VÉRITÉ : MODELE-PRIX-REPRISE.md (modèle économique) et
// ARCHITECTURE.md §3, §3bis, §3ter.
//
//   crédit = min( prix_variante(état) × taux × boosts , prix × 25% )   (arrondi à l'euro, 1 € min)
//   modèle Prio : crédit = min( crédit_non_arrondi × (1 + boost_prio) , prix × 35% )
//
// Révision 2026-10-12 (décision Benoni) : plus de plancher de 5 € — même
// quelques euros, « c'est déjà ça ».
// Révision 2026-10-13 (décision Benoni) : PLUS AUCUN SEUIL DE PRIX — catalogue et
// fiches provisoires sont repris quel que soit le prix (1 € minimum). Auparavant :
// 30 € neuf, soit 21 € de revente pour le catalogue.
//
// LOGIQUE (refonte 2026-09-07) : le taux mesure la ROTATION du modèle, pas sa
// cherté ni notre besoin de réappro. Fourchette relevée le 2026-09-17 de
// [3 %, 15 %] à [10 %, 25 %] : les crédits étaient trop faibles pour motiver
// l'envoi (un Womanizer Next revendu 139 € ne rapportait que 17 € au mieux).
//
// Ce qu'on veut racheter, c'est ce qui se revend. Sur du reconditionné où la
// majorité des pièces sont uniques, le signal qui prédit « ça repartira » est
// le nombre d'unités vendues sur 90 jours. L'ancien modèle pilotait le taux
// par la COUVERTURE de stock (stock ÷ vélocité) — une logique de réappro qui
// répond à « qu'est-ce qui nous manque ? », pas à « qu'est-ce qui tourne ? » ;
// un bestseller correctement approvisionné y scorait 0. Le prix, lui, entrait
// deux fois (multiplicande ET signal), si bien que le taux ne mesurait au bout
// du compte que la cherté de l'article.
//
// Règles économiques (dans l'ordre d'application) :
// 1. taux ∈ [TAUX_MIN, TAUX_PLAFOND] piloté par les ventes 90 j du modèle
//    (courbe log : VENTES_REF_PLAFOND ventes = taux max), puis tempéré par le
//    malus de sur-stock. (Les campagnes par catégorie, crans additifs, ont été
//    retirées le 2026-10-23 : bornées par le plafond de 25 %, elles ne boostaient
//    rien sur les modèles qui tournent déjà, précisément ceux qu'on veut.)
// 2. boost_fidélité : multiplicateur ≥ 1 si le client a déjà acheté.
// 3. accessoires manquants : −15 %.
// 4. PLAFOND DUR ABSOLU : le crédit ne dépasse JAMAIS TAUX_PLAFOND (25 %) du
//    prix de revente de la variante — il s'applique APRÈS tous les boosts.
// 4bis. MODÈLE PRIO (2026-10-23, src/lib/prio.js) : +X % du crédit APRÈS le plafond
//    de 25 %, borné à PLAFOND_PRIO (35 %) du prix. Temporaire, posé au lab.
// 5. (Supprimé le 2026-10-12) Plus de plancher : le crédit est le montant
//    calculé, arrondi à l'euro (1 € minimum si éligible — 2026-10-12, après un
//    bref passage au centime). Ancienne règle : tout article éligible
//    rapportait au moins 5 €. À 25 %, le plancher ne dépassait le plafond qu'en
//    dessous de 20 €, soit uniquement sur les exceptions de rotation / tag.
// 6. ÉLIGIBILITÉ : tout article ayant un prix de revente est repris (2026-10-13).
//    Les exclusions restent celles du catalogue (_eligibilite.js, « Exclu reprise »).
//    « Prioritaire » (tag ou rotation) n'ouvre plus rien : il ne sert qu'à l'affichage.
//
// Le module est pur (aucun accès réseau) pour être testable et partagé
// entre la fonction serveur (recalcul anti-triche) et l'estimation client.

export const TAUX_MIN = 0.10;          // taux plancher (modèle qui ne tourne pas)
export const TAUX_PLAFOND = 0.25;      // plafond dur ABSOLU (coût ≤ 25 % du prix de vente)
export const PRIX_NEUF_MIN_REPRISE = 0;   // € NEUF — plus de limite depuis le 2026-10-13 (était 30)
export const RATIO_REVENTE_NEUF = 0.7;    // prix de revente Rejouis ≈ 70 % du neuf
// € de revente — équivalent catalogue (0 = aucun seuil). Était 21 €, puis 30 € de revente (≈ 43 € neuf).
export const PRIX_MIN_REPRISE = Math.round(PRIX_NEUF_MIN_REPRISE * RATIO_REVENTE_NEUF);
export const MALUS_ACCESSOIRES = 0.85; // −15 % si accessoires manquants
// Plafond du crédit d'un modèle Prio (recherché), boost compris. Le boost Prio passe
// au-dessus du plafond normal pour rester visible sur les modèles qui tournent déjà
// (déjà à 25 %), mais le coût reste borné (décision 2026-10-23).
export const PLAFOND_PRIO = 0.35;

// Ventes sur 90 j qui saturent le score de demande. Calé sur la distribution
// réelle du catalogue (543 unités / 90 j, ~250 modèles vendus) : 10 ventes
// place un modèle dans le top 6, 5 ventes dans le top 16.
export const VENTES_REF_PLAFOND = 10;

// Ventes sur 90 j à partir desquelles un modèle reste éligible même revendu
// sous PRIX_MIN_REPRISE (ex. Starlet 3 à 26 €, Satisfyer Pro 2 Gen 2 à 20 €).
export const VENTES_MIN_PRIORITAIRE = 5;

// Garde-fou de l'éligibilité AUTOMATIQUE : sous ce prix, aucune rotation ne
// suffit à rendre un article repris. Les règles catalogue laissent passer les
// « produit-hors-jouet » (choix assumé : accessoires, livres), et certains
// tournent très vite pour trois francs six sous — le chargeur magnétique
// Womanizer se vend 12 fois par trimestre à 2,50 €. Conçu quand un plancher de
// 5 € existait (il aurait coûté 200 % du prix) ; conservé sans plancher pour ne
// pas racheter des accessoires à quelques euros sur le seul critère de rotation.
// Le tag manuel « Reprise prioritaire » n'y est PAS soumis : c'est une décision
// humaine prise fiche par fiche, elle garde le dernier mot.
export const PRIX_MIN_ABSOLU = 15;

// Anti-sur-stock : au-delà de COUVERTURE_OK jours de stock, le taux décroît
// jusqu'à MALUS_SURSTOCK_MAX à COUVERTURE_MAX jours. Ce n'est PAS un bonus de
// réappro (un modèle bien couvert n'est pas pénalisé), seulement un frein sur
// les modèles dont on a déjà des années de stock (ex. Sync 2 : 51 en stock
// pour 3 ventes/90 j, soit plus de 4 ans de couverture).
export const COUVERTURE_OK = 120;      // jours — en deçà, aucun malus
export const COUVERTURE_MAX = 365;     // jours — au-delà, malus maximal
export const MALUS_SURSTOCK_MAX = 0.6; // −40 % sur le taux

// clamp utilitaire
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
// Arrondi à l'euro, jamais 0 € pour un article éligible (décision Benoni 2026-10-12).
const arrondiEuro = (v) => Math.max(1, Math.round(v));

/**
 * Score de demande → [0,1] à partir des ventes du modèle sur 90 jours.
 * Courbe logarithmique : le catalogue est une longue traîne (l'immense
 * majorité des modèles vend 1 unité/90 j), une échelle linéaire écraserait
 * tout le milieu de gamme sur 0. Avec le log : 1 vente ≈ 0,29 · 3 ≈ 0,58 ·
 * 5 ≈ 0,75 · 10 et plus = 1.
 * @param {number} ventes90 unités vendues sur les 90 derniers jours
 */
export function scoreDemande(ventes90) {
  const v = Number(ventes90) || 0;
  if (v <= 0) return 0;
  return clamp(Math.log(1 + v) / Math.log(1 + VENTES_REF_PLAFOND), 0, 1);
}

/**
 * Malus de sur-stock ∈ [MALUS_SURSTOCK_MAX, 1] — multiplicateur du taux.
 * couverture = stock ÷ vélocité (jours de stock restants au rythme actuel).
 * @param {number} stock    unités en stock, TOUS états confondus (niveau modèle)
 * @param {number} ventes90 unités vendues sur 90 j
 */
export function malusSurstock(stock, ventes90) {
  const s = Number(stock) || 0;
  const v = Number(ventes90) || 0;
  if (s <= 0) return 1;                    // rien en stock → aucun frein
  if (v <= 0) return MALUS_SURSTOCK_MAX;   // du stock, zéro vente → frein maximal
  const jours = s / (v / 90);
  if (jours <= COUVERTURE_OK) return 1;
  if (jours >= COUVERTURE_MAX) return MALUS_SURSTOCK_MAX;
  const part = (jours - COUVERTURE_OK) / (COUVERTURE_MAX - COUVERTURE_OK);
  return 1 - part * (1 - MALUS_SURSTOCK_MAX);
}

/**
 * Un modèle est-il « prioritaire » — c'est-à-dire repris même sous
 * PRIX_MIN_REPRISE ? Deux portes, et elles n'ont pas les mêmes garde-fous :
 *  - `tag` : le tag Shopify « Reprise prioritaire », posé à la main fiche par
 *    fiche. Décision humaine → override total, aucun plancher de prix.
 *  - `rotation` : ventes 90 j ≥ VENTES_MIN_PRIORITAIRE, rafraîchi chaque nuit.
 *    Automatique → borné par PRIX_MIN_ABSOLU (cf. le chargeur à 2,50 €).
 * @returns {{ tag:boolean, rotation:boolean, prioritaire:boolean }}
 */
export function motifsPrioritaire({ ventes90 = 0, tagPrioritaire = false } = {}) {
  const tag = Boolean(tagPrioritaire);
  const rotation = (Number(ventes90) || 0) >= VENTES_MIN_PRIORITAIRE;
  return { tag, rotation, prioritaire: tag || rotation };
}

/** Raccourci booléen — un modèle est-il prioritaire, peu importe le motif. */
export function estModelePrioritaire(opts) {
  return motifsPrioritaire(opts).prioritaire;
}

/**
 * Un article est-il repris en crédit ? Oui dès qu'il a un prix de revente : plus
 * aucun seuil de prix depuis le 2026-10-13 (décision Benoni). Les paramètres
 * ventes90 / tagPrioritaire sont gardés pour ne pas casser les appelants.
 */
export function estEligibleReprise({ prix } = {}) {
  return (Number(prix) || 0) > 0;
}

/**
 * Taux de reprise ∈ [TAUX_MIN, TAUX_PLAFOND].
 * Le PRIX n'entre PAS dans le taux : il est déjà le multiplicande du crédit,
 * l'y remettre en signal le compterait deux fois et ferait du taux une mesure
 * de cherté au lieu d'une mesure de désirabilité.
 * @param {object} p
 * @param {number} [p.ventes90]  unités vendues sur 90 j (metafield reprise_ventes_90j)
 * @param {number} [p.stock]     stock du modèle, tous états confondus
 * @returns {number} taux dans [TAUX_MIN, TAUX_PLAFOND]
 */
export function tauxReprise({ ventes90 = 0, stock = 0 }) {
  const base = TAUX_MIN + scoreDemande(ventes90) * (TAUX_PLAFOND - TAUX_MIN);
  const taux = base * malusSurstock(stock, ventes90);
  return clamp(taux, TAUX_MIN, TAUX_PLAFOND);
}

/**
 * Crédit final — applique la totalité du modèle économique (voir en-tête).
 * Le plafond de 25 % s'applique APRÈS le boost fidélité : aucun cumul ne peut le
 * dépasser. Seul le boost Prio passe au-dessus, borné à PLAFOND_PRIO.
 * @param {number} [p.boostPrio=0] boost Prio en fraction du crédit (0,3 = +30 %), cf. prio.js
 * @returns {{ credit:number, creditSansBoost:number, taux:number, tauxEffectif:number,
 *             boostFidelite:number,
 *             eligibleReprise:boolean, prioritaire:boolean }}
 */
export function calculeCredit({
  prix,
  stock = 0,
  ventes90 = 0,
  boostPrio = 0,
  boostFidelite = 1,
  accessoiresIncomplets = false,
  prioritaire = false,
}) {
  const estPrio = estModelePrioritaire({ ventes90, tagPrioritaire: prioritaire });
  const eligibleReprise = estEligibleReprise({ prix, ventes90, tagPrioritaire: prioritaire });
  const taux = tauxReprise({ ventes90, stock });

  let brut = prix * taux * boostFidelite;
  if (accessoiresIncomplets) brut *= MALUS_ACCESSOIRES;

  // Plafond dur ABSOLU — après tous les boosts et malus.
  brut = Math.min(brut, prix * TAUX_PLAFOND);

  // Inéligible → 0, jamais le montant brut. Le renvoyer quand même obligeait
  // chaque appelant à penser à tester `eligibleReprise` (demande.js le faisait,
  // rien ne garantissait que le suivant y penserait) : un crédit qu'on ne doit
  // jamais verser n'a pas à exister dans la sortie.
  const creditSansBoost = eligibleReprise ? arrondiEuro(brut) : 0;
  // Boost Prio : au-dessus du plafond normal, borné à PLAFOND_PRIO du prix.
  const avecPrio = boostPrio > 0 ? Math.max(brut, Math.min(brut * (1 + boostPrio), prix * PLAFOND_PRIO)) : brut;
  const credit = eligibleReprise ? arrondiEuro(avecPrio) : 0;

  return {
    credit,
    creditSansBoost,
    taux,
    tauxEffectif: prix > 0 ? credit / prix : 0,
    boostFidelite,
    eligibleReprise,
    prioritaire: estPrio,
  };
}

// ── Fiches provisoires (sas) ───────────────────────────────────────────────
// Produit créé par le client depuis une URL, absent de Shopify : aucune vente ni
// prix d'occasion connus, donc le modèle par rotation ci-dessus lui donnerait le
// plancher quel que soit le produit. Règle dédiée (décision Benoni 2026-09-16) :
// un pourcentage du prix NEUF lu sur la fiche, plafonné — le plafond borne ce
// qu'une page trafiquée pourrait faire gagner. Le taux vaut le taux plancher du
// modèle général appliqué au prix de revente Rejouis (70 % du neuf) :
// 10 % × 70 % = 7 % du neuf — une fiche provisoire n'a encore aucune vente.
// Mêmes seuils que le lab
// (_fiche-provisoire.js) : sous PRIX_NEUF_MIN la fiche est refusée (→ don),
// au-dessus de PRIX_NEUF_MAX elle part en vérification manuelle.
export const PROVISOIRE_TAUX_NEUF = 0.07;
export const PROVISOIRE_CREDIT_MAX = 15;     // €
export const PROVISOIRE_PRIX_NEUF_MIN = PRIX_NEUF_MIN_REPRISE;  // € — 0 depuis le 2026-10-13 (était 30, puis 43)
export const PROVISOIRE_PRIX_NEUF_MAX = 400; // €

/**
 * Crédit d'une fiche provisoire.
 * @returns {{ credit:number, eligibleReprise:boolean }}
 */
export function creditFicheProvisoire({ prixNeuf, accessoiresIncomplets = false }) {
  const neuf = Number(prixNeuf) || 0;
  const eligibleReprise = neuf > 0 && neuf >= PROVISOIRE_PRIX_NEUF_MIN && neuf <= PROVISOIRE_PRIX_NEUF_MAX;
  if (!eligibleReprise) return { credit: 0, eligibleReprise };
  let brut = Math.min(neuf * PROVISOIRE_TAUX_NEUF, PROVISOIRE_CREDIT_MAX);
  if (accessoiresIncomplets) brut *= MALUS_ACCESSOIRES;
  return { credit: arrondiEuro(brut), eligibleReprise };
}
